import { Component, ElementRef, computed, input, output, signal, viewChild } from '@angular/core';

import { isPdfFile } from './pricing-supplement.types';

/** Single-PDF drag & drop picker with a removable file chip. */
@Component({
  selector: 'app-pricing-supplement-file-picker',
  templateUrl: './pricing-supplement-file-picker.html',
  styleUrl: './pricing-supplement-file-picker.css',
})
export class PricingSupplementFilePicker {
  readonly inputId = input.required<string>();
  readonly disabled = input(false);
  readonly hint = input('');
  readonly selectionChange = output<File | null>();

  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  protected readonly file = signal<File | null>(null);
  protected readonly isDragOver = signal(false);
  protected readonly error = signal('');
  protected readonly fileSize = computed(() => formatFileSize(this.file()?.size ?? 0));

  reset(): void {
    this.file.set(null);
    this.error.set('');
    this.isDragOver.set(false);
    const element = this.fileInput()?.nativeElement;
    if (element) {
      element.value = '';
    }
  }

  protected browse(): void {
    if (!this.disabled()) {
      this.fileInput()?.nativeElement.click();
    }
  }

  protected onInputChange(event: Event): void {
    const element = event.target as HTMLInputElement | null;
    this.accept(element?.files?.item(0) ?? null);
    if (element) {
      element.value = '';
    }
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (!this.disabled()) {
      this.isDragOver.set(true);
    }
  }

  protected onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(false);
  }

  protected onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(false);
    if (this.disabled()) {
      return;
    }

    const files = event.dataTransfer?.files;
    if (files && files.length > 1) {
      this.error.set('Only one PDF file can be uploaded.');
      return;
    }
    this.accept(files?.item(0) ?? null);
  }

  protected remove(): void {
    this.reset();
    this.selectionChange.emit(null);
  }

  private accept(file: File | null): void {
    if (!file) {
      return;
    }

    if (!isPdfFile(file)) {
      this.error.set('Only PDF files are allowed.');
      return;
    }

    this.error.set('');
    this.file.set(file);
    this.selectionChange.emit(file);
  }
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
